import type React from 'react';

import { Button } from '@change/ds-core-ui/Button';
import { ProfileCard } from '@change/ds-core-ui/ProfileCard';
import { Text } from '@change/ds-core-ui/Text';
import { CheckmarkVerifiedIcon } from '@change/ds-icons/CheckmarkVerifiedIcon';
import { DecisionMakerIcon } from '@change/ds-icons/DecisionMakerIcon';

import { Translate, TranslatePlural, useI18n } from '@change-corgi/core-react/i18n';

import type { DecisionMakerCardData } from './hooks/useDecisionMakerCard';
import { useDecisionMakerCard } from './hooks/useDecisionMakerCard';

type CardProps = {
	decisionMaker: DecisionMakerCardData;
	trackClick: (target: 'name' | 'view_profile') => void;
};

const Name: React.FC<{ decisionMaker: DecisionMakerCardData }> = ({ decisionMaker }) => {
	const { translate } = useI18n();

	return (
		<span className="inline-flex items-center gap-2">
			{decisionMaker.name}
			{decisionMaker.verified && (
				<CheckmarkVerifiedIcon
					size="small"
					className="text-icon-information shrink-0"
					aria-label={translate('corgi.search.decision_maker_card.verified')}
					data-testid="search-dm-card-verified"
				/>
			)}
		</span>
	);
};

const FullCard: React.FC<CardProps> = ({ decisionMaker, trackClick }) => (
	<ProfileCard
		data-qa="search-dm-card"
		orientation="horizontal"
		className="border-stroke-weaker mb-4 w-full max-w-none! border md:mb-6"
		href={decisionMaker.profileUrl}
		onNameClick={() => trackClick('name')}
		name={<Name decisionMaker={decisionMaker} />}
		// the name is right next to it, so the avatar is decorative
		imgAlt=""
		userRole={decisionMaker.title}
		avatar={decisionMaker.avatarUrl}
		icon={<DecisionMakerIcon />}
		iconPosition="right"
		label={
			<Text as="p" variant="body" size="small" className="text-typography-weak">
				<TranslatePlural
					value="corgi.search.decision_maker_card.label"
					count={decisionMaker.petitionsCount}
					replacements={{ countLocalized: decisionMaker.petitionsCountLocalized }}
				/>
			</Text>
		}
	>
		<Button
			as="a"
			href={decisionMaker.profileUrl}
			variant="secondary"
			size="small"
			className="self-start max-md:w-full!"
			data-qa="search-dm-card-view-profile"
			onClick={() => trackClick('view_profile')}
		>
			<Translate value="corgi.search.decision_maker_card.view_profile" />
		</Button>
	</ProfileCard>
);

/**
 * One of several namesakes side by side: no button, the name links to the profile
 */
const CompactCard: React.FC<CardProps> = ({ decisionMaker, trackClick }) => (
	<ProfileCard
		data-qa="search-dm-card-compact"
		orientation="horizontal"
		className="border-stroke-weaker h-full w-full max-w-none! border"
		href={decisionMaker.profileUrl}
		onNameClick={() => trackClick('name')}
		name={<Name decisionMaker={decisionMaker} />}
		imgAlt=""
		userRole={<span className="line-clamp-2">{decisionMaker.title}</span>}
		avatar={decisionMaker.avatarUrl}
		label={
			<Text as="p" variant="body" size="small" className="text-typography-weak">
				<TranslatePlural
					value="corgi.search.decision_maker_card.compact_label"
					count={decisionMaker.petitionsCount}
					replacements={{ countLocalized: decisionMaker.petitionsCountLocalized }}
				/>
			</Text>
		}
	/>
);

export const DecisionMakerCard: React.FC<EmptyProps> = () => {
	const {
		data: { decisionMakers },
		actions: { trackClick },
	} = useDecisionMakerCard();
	const { translate } = useI18n();

	if (!decisionMakers.length) return null;

	if (decisionMakers.length === 1) {
		const [decisionMaker] = decisionMakers;
		if (!decisionMaker) return null;
		return <FullCard decisionMaker={decisionMaker} trackClick={(target) => trackClick(decisionMaker.id, target)} />;
	}

	return (
		<ul
			data-qa="search-dm-cards"
			aria-label={translate('corgi.search.decision_maker_card.list_label')}
			// a row on desktop; on mobile, a row that scrolls sideways so the results stay close to the top
			className="-mx-4 mb-4 flex snap-x snap-mandatory scroll-px-4 gap-3 overflow-x-auto px-4 md:mx-0 md:mb-6 md:grid md:auto-cols-fr md:grid-flow-col md:overflow-visible md:px-0"
		>
			{decisionMakers.map((decisionMaker) => (
				<li key={decisionMaker.id} className="w-4/5 shrink-0 snap-start md:w-auto">
					<CompactCard decisionMaker={decisionMaker} trackClick={(target) => trackClick(decisionMaker.id, target)} />
				</li>
			))}
		</ul>
	);
};
